import { AdminOnlyBadge } from '@/components/admin-only-badge';
import { MobilePageShell } from '@/components/app/mobile-page-shell';
import { Card } from '@/components/ui/card';
import { branchHandle } from '@/features/organization-locations/branch-path';
import { useActiveLocation } from '@/features/organization-locations/use-active-location';
import { useActiveOrganization } from '@/features/organization/api/get-active-organization/get-active-organization.hook';
import { useClaireDesigner } from '@/features/public-sites/claire-designer-flag';
import { isProductionRuntime } from '@/lib/app-env';
import {
  type DashboardNavItem,
  filterNavForBookingDestination,
  filterNavForFeatureFlags,
  filterNavForProductionRuntime,
  findDashboardNavSection,
  isAdminOnlyBorradhNavigation,
  resolveNavUrl,
} from '@/lib/dashboard-nav';
import { ROUTES } from '@/lib/route-paths';
import { useSession } from '@/lib/session';
import { useEnabledNavFlags } from '@/lib/use-enabled-nav-flags';
import { CLAIRE_DESIGNER_FLAG } from '@borradh-workspace/contracts';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { ChevronRight } from 'lucide-react';
import { useCallback } from 'react';

export const Route = createFileRoute('/_authed/dashboard/more/$section')({
  component: MoreSectionPage,
});

/** Sub-navigation for one More section (Sales, Catalog, Marketing, …). */
function MoreSectionPage() {
  const { section: slug } = Route.useParams();
  const navigate = useNavigate();
  const { enabled: designerEnabled, loading: designerLoading } =
    useClaireDesigner();
  const configuredSection = findDashboardNavSection(
    slug === 'website' && designerEnabled ? 'booking-page' : slug
  );
  const { data: organization } = useActiveOrganization();
  const { data: session } = useSession();
  const isImpersonating = Boolean(session?.session?.impersonatedBy);
  const availableFlags = useEnabledNavFlags();
  const enabledFlags = new Set(availableFlags);
  if (designerEnabled) enabledFlags.add(CLAIRE_DESIGNER_FLAG);
  const section = configuredSection
    ? filterNavForBookingDestination(
        filterNavForProductionRuntime(
          filterNavForFeatureFlags([configuredSection], enabledFlags),
          isProductionRuntime()
        ),
        organization?.bookingDestination,
        isImpersonating
      )[0]
    : undefined;
  const showAdminOnlyBadges = isAdminOnlyBorradhNavigation(
    organization?.bookingDestination,
    isImpersonating
  );

  const goBack = useCallback(() => navigate({ to: ROUTES.more }), [navigate]);

  if (slug === 'website' && designerLoading) {
    return (
      <MobilePageShell
        contentClassName="px-4 pb-6"
        onBack={goBack}
        title="Website"
      >
        <p role="status" className="text-sm text-muted-foreground">
          Loading…
        </p>
      </MobilePageShell>
    );
  }

  if (!section) {
    return (
      <div className="mx-auto w-full max-w-2xl px-4 py-6">
        <p className="text-sm text-muted-foreground">
          That section doesn&apos;t exist.
        </p>
        <Link to={ROUTES.more} className="text-sm underline">
          Back to More
        </Link>
      </div>
    );
  }

  const items = section.items ?? [];

  return (
    <>
      <title>{`${section.title} | Borrah`}</title>
      <MobilePageShell
        contentClassName="px-4 pb-6"
        onBack={goBack}
        title={section.title}
      >
        <Card className="mx-auto w-full max-w-2xl gap-0 overflow-hidden py-0">
          <ul className="flex flex-col divide-y">
            {items.map((item) => (
              <MoreSectionRow
                adminOnly={
                  showAdminOnlyBadges &&
                  Boolean(section.borradhOnly || item.borradhOnly)
                }
                item={item}
                key={item.url}
              />
            ))}
          </ul>
        </Card>
      </MobilePageShell>
    </>
  );
}

function MoreSectionRow({
  adminOnly,
  item,
}: {
  adminOnly: boolean;
  item: DashboardNavItem;
}) {
  // The nav config holds branch destinations UN-PREFIXED (one config serves
  // every branch), so the branch has to be added at render. Linking `item.url`
  // raw sent every row on this page — Sales, Catalog, Marketing, Team,
  // Inventory — through the compatibility splat's redirect.
  const { location } = useActiveLocation();
  const to = resolveNavUrl(item, location ? branchHandle(location) : null);

  return (
    <li>
      <Link
        to={to}
        className="flex items-center gap-3 px-4 py-3.5 transition active:bg-accent/50"
      >
        <span className="min-w-0 flex-1">
          <span className="block truncate text-left text-sm">{item.title}</span>
          {item.mobileReady === false ? (
            <span className="block truncate text-xs text-muted-foreground">
              Best viewed on a larger screen
            </span>
          ) : null}
        </span>
        {adminOnly ? <AdminOnlyBadge /> : null}
        <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
      </Link>
    </li>
  );
}
